// =====================================================================
// TransferPitch — "All Listed Requests" LIST view (card ↔ list toggle).
//
// This is the list/accordion alternative to the existing card grid on
// AgentTenders.js. It deliberately mirrors the Transfer Windows page
// pattern (src/pages/TransferWindows.js): a single table card where each
// row is clickable and toggles a single-id `expanded`; the expanded row
// renders a detail panel into a `colSpan` cell underneath. Same UX as the
// page the user already knows.
//
// Field formatting reuses the shared, null-safe helpers in
// utils/tenderFormatters.js (the same ones RequestPreviewModal uses) so
// the list never diverges from the card / preview modal.
//
// The row actions (Pitch / Save) reuse the parent's existing handlers so
// behaviour is identical to the card view.
// =====================================================================
import React, { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import CountryFlag from "../transfer-windows/CountryFlag";
import AttachedDocLink from "./AttachedDocLink";
import PostedByAgentLine from "../club/PostedByAgentLine";
import {
  formatPositionLabel,
  formatFeeBlock,
  formatDivisionLine,
  formatSalary,
  getSalaryKind,
  formatRange,
  formatFoot,
  formatHeight,
  prettyValue,
  shouldRender,
} from "../../utils/tenderFormatters";

// One detail field — only renders when there's a value.
function DetailField({ label, value }) {
  if (!shouldRender(value)) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] text-[#242426B2] font-bold uppercase tracking-wide">
        {label}
      </span>
      <span className="text-[13px] text-[#0B0B0B] font-medium break-words">
        {value}
      </span>
    </div>
  );
}

// The accordion body for an expanded row. Mirrors the data shown in the
// card / preview modal, without truncation. `actions` is an optional node
// (the role/tab-aware CTA button(s), wired to the card's existing handlers)
// rendered in the panel's action area — keeps the clean list layout while
// surfacing the same primary action(s) the card has.
function RequestDetailPanel({ tender, actions }) {
  const ageRange = formatRange(tender.min_age, tender.max_age, "years");
  const salaryKind = getSalaryKind(tender);
  const salaryLine = formatSalary(tender);

  return (
    <div className="flex flex-col gap-4 py-2">
      {shouldRender(tender.description) && (
        <div>
          <div className="text-[11px] text-[#242426B2] font-bold uppercase tracking-wide mb-1">
            Description
          </div>
          {/* break-words as well as pre-wrap: pre-wrap keeps the author's line
              breaks but still cannot break a token with no spaces in it — a
              club name like Bayerischer-Fussball-Verband-…, a URL or an e-mail
              address in the brief. Without it that one token runs past the
              right edge and the rest of the word is simply gone. */}
          <p className="text-[13px] text-[#242426] leading-relaxed whitespace-pre-wrap break-words">
            {tender.description}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DetailField label="Request Type" value={prettyValue(tender.type_of_tender)} />
        <DetailField label="Category" value={prettyValue(tender.tender_category)} />
        <DetailField label="Age limit" value={ageRange} />
        <DetailField label="Foot" value={formatFoot(tender.foot)} />
        <DetailField label="Min. Height" value={formatHeight(tender.minimum_height)} />
        <DetailField
          label="Games played last season"
          value={tender.matches_played_last_season}
        />
        <DetailField
          label={salaryKind ? `${salaryKind} Monthly salary` : "Monthly salary"}
          value={salaryLine}
        />
        <DetailField label="Recruiting for" value={prettyValue(tender.recruiting_for)} />
      </div>

      {shouldRender(tender.key_requirements) && (
        <div>
          <div className="text-[11px] text-[#242426B2] font-bold uppercase tracking-wide mb-1">
            Key Requirements
          </div>
          {/* break-words for the same reason as Description above. */}
          <p className="text-[13px] text-[#242426] leading-relaxed whitespace-pre-wrap break-words">
            {tender.key_requirements}
          </p>
        </div>
      )}

      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

// Pill colour mirrors the card's type_of_tender colours.
function typePillClass(type) {
  if (type === "buy") return "bg-[#049669] text-white";
  if (type === "loan") return "bg-[#FF9500] text-white";
  return "bg-blue-500 text-white";
}

const ListedRequestsListView = ({
  tenders,
  onPreview,
  onToggleSave,
  savingIds = new Set(),
  // Optional render-prop returning the role/tab-aware CTA button(s) for a row,
  // wired to the CARD's existing handlers (no reimplementation). Rendered in
  // the clean detail-panel action area — the previous list layout is kept;
  // only the button(s) are added. `renderActions(tender) => ReactNode`.
  renderActions,
  // id of the single newest tender (max created_at) to badge "NEWEST".
  newestId,
  // Optional render-prop for EXTRA status badges next to NEWEST in the
  // Position cell (`renderBadges(tender) => ReactNode`). The club's Drafts tab
  // uses it to carry the Draft / Scheduled pill into list mode, so a draft is
  // never indistinguishable from a live request just because the view toggled.
  // Left out -> the cell renders exactly as before.
  renderBadges,
  // Optional render-prop for row-level action button(s) in the COLLAPSED
  // row's Actions cell, before the preview/save cluster
  // (`renderRowActions(tender) => ReactNode`). The club's Archive tab uses it
  // for the row's Reactivate button. Left out -> the cell renders exactly as
  // before.
  renderRowActions,
}) => {
  // Single-id accordion, same as the Transfer Windows page.
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-x-auto overflow-y-hidden mx-4 md:mx-12 my-4">
      <table className="w-full border-collapse min-w-[720px]">
        <thead>
          <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Position
            </th>
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Club
            </th>
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Division
            </th>
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Fee / Market Value
            </th>
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Type
            </th>
            <th className="text-center text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {tenders.map((tender) => {
            const isOpen = expanded === tender.id;
            const fee = formatFeeBlock(tender);
            const divisionLine = formatDivisionLine(tender);
            return (
              <React.Fragment key={tender.id}>
                <tr
                  onClick={() => toggle(tender.id)}
                  className={`cursor-pointer transition border-b border-[#F1F5F9] ${
                    isOpen ? "bg-[#F8FAFC]/50" : "hover:bg-[#F8FAFC]"
                  }`}
                >
                  {/* Position */}
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-base text-[#0F172B] capitalize">
                        {formatPositionLabel(tender.position) || "-"}
                      </span>
                      {newestId != null && tender.id === newestId && (
                        <span className="px-2 py-0.5 bg-[#2283FF] text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                          NEWEST
                        </span>
                      )}
                      {renderBadges ? renderBadges(tender) : null}
                    </div>
                  </td>
                  {/* Club */}
                  <td className="px-6 py-4">
                    {/* min-w-0 on the row + break-words on the name: a club
                        name is one long token often enough (hyphenated German
                        and Dutch names) that without it the cell is pushed out
                        and the tail of the name is unreadable. */}
                    <div className="flex items-center gap-2 min-w-0">
                      {tender.club?.logo && (
                        <img
                          src={tender.club.logo}
                          alt={tender.club?.name || ""}
                          className="w-7 h-7 object-contain shrink-0"
                          referrerPolicy="no-referrer"
                        />
                      )}
                      <span className="text-sm text-[#242426] font-medium min-w-0 break-words">
                        {tender.club?.name || "-"}
                      </span>
                    </div>
                    {/* DECISION 009 — the club's "My Requests" has a card/list
                        toggle, and an attribution line is never removed: it
                        must survive the toggle, or an agency-posted request
                        looks like the club's own in list mode. Renders only
                        when the backend sends agent_attribution. */}
                    <PostedByAgentLine
                      attribution={tender.agent_attribution}
                      className="mt-1"
                    />
                  </td>
                  {/* Division */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#45556C]">
                        {divisionLine || "-"}
                      </span>
                      {(tender.division_country?.name ||
                        tender.division_country?.image_url) && (
                        <CountryFlag
                          country={tender.division_country?.name}
                          imageUrl={tender.division_country?.image_url}
                          width={18}
                          height={12}
                        />
                      )}
                    </div>
                  </td>
                  {/* Fee / Market value */}
                  <td className="px-6 py-4 text-sm text-[#0F172B]">
                    {shouldRender(fee.value) ? fee.value : "-"}
                  </td>
                  {/* Type pill */}
                  <td className="px-6 py-4">
                    <span
                      className={`text-xs px-3 py-1 rounded-full capitalize ${typePillClass(
                        tender.type_of_tender,
                      )}`}
                    >
                      {tender.type_of_tender?.toUpperCase() || "-"}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-1">
                      {renderRowActions ? renderRowActions(tender) : null}
                      {/* Attached-document paperclip (when present) — same
                          indicator + same opened url as the card. NEWEST lives
                          in the Position column, so no collision here. */}
                      <AttachedDocLink tender={tender} size={4} />
                      {onPreview && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreview(tender);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          aria-label="Preview request"
                          title="Quick preview"
                        >
                          <Search className="w-4 h-4 text-[#535370]" />
                        </button>
                      )}
                      {onToggleSave && (
                        <button
                          type="button"
                          disabled={savingIds.has(tender.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleSave(tender);
                          }}
                          className={`p-2 hover:bg-gray-100 rounded-lg ${
                            savingIds.has(tender.id)
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          aria-label={tender.is_saved ? "Unsave request" : "Save request"}
                        >
                          <svg
                            className={`w-4 h-4 ${
                              tender.is_saved
                                ? "text-[#2283FF] fill-current"
                                : "text-[#1D1B20]"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                            />
                          </svg>
                        </button>
                      )}
                      <ChevronDown
                        className={`w-4 h-4 text-[#45556C] transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="border-b border-[#F1F5F9]">
                    <td colSpan={6} className="px-6 pb-5 pt-1 bg-[#F8FAFC]/50">
                      {/* The table is `min-w-[720px]` inside a sideways
                          scroller, which is right for six COLUMNS. The expanded
                          panel is not columns, it is prose plus buttons — and
                          inheriting the 720px made every line of Description /
                          Key Requirements wrap at 720px on a 320px phone, so
                          354-468px of each line sat off-screen and "Post now"
                          started 273px past the right edge (measured; Marin's
                          320px screenshot).

                          So the panel is bound to the width that is actually ON
                          SCREEN instead: viewport minus the card's own gutters
                          (mx-4 / md:mx-12) minus this cell's px-6. `max-w-full`
                          keeps it inside the cell, so as soon as the table stops
                          scrolling (~816px viewport upwards) the cell is the
                          smaller of the two and the panel renders exactly as
                          before — verified at 1440 and 2560.

                          `sticky left-0` keeps it against the visible left edge
                          while the row itself is scrolled sideways, so the text
                          stays readable instead of sliding away.

                          max-width, NOT width: this table is auto-layout, so a
                          declared `width: calc(100vw - …)` is fed into the
                          column-width calculation and drags the whole table out
                          to that number — measured, it pushed the table 69px
                          past the shell at 2560. A max-width only ever caps,
                          never inflates, so above the scroll threshold the cell
                          is the smaller of the two and nothing moves. */}
                      <div className="sticky left-0 max-w-[calc(100vw-5rem)] md:max-w-[calc(100vw-9rem)]">
                        <RequestDetailPanel
                          tender={tender}
                          actions={renderActions ? renderActions(tender) : null}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ListedRequestsListView;
